'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, Filter, LayoutGrid, List, Sparkles, X, ChevronRight, ArrowRight, Shield, Code, Cpu, FileText, Globe, LineChart, Box } from 'lucide-react';
import { ALL_TOOLS, ToolEntry } from '@/lib/unified-tools';

const CATEGORIES = [
  { id: 'All Utilities', icon: <Box size={16} /> },
  { id: 'Financial', icon: <LineChart size={16} /> },
  { id: 'Content', icon: <FileText size={16} /> },
  { id: 'Security', icon: <Shield size={16} /> },
  { id: 'Developer', icon: <Code size={16} /> },
  { id: 'AI & Marketing', icon: <Cpu size={16} /> },
  { id: 'Web & SEO', icon: <Globe size={16} /> }
];

export default function ToolDiscovery() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All Utilities');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const filteredTools = useMemo(() => {
    return ALL_TOOLS.filter(tool => {
      const matchesSearch = 
        tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (tool.keywords && tool.keywords.some(k => k.toLowerCase().includes(searchQuery.toLowerCase())));
      
      const matchesCategory = activeCategory === 'All Utilities' || tool.category === activeCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, activeCategory]);

  const featuredTools = useMemo(() => ALL_TOOLS.filter(t => t.isNew).slice(0, 4), []);

  return (
    <div className="discovery-container">
      {/* Search & Filter Header */}
      <div className="discovery-header" style={{
        background: '#f8fafc',
        borderBottom: '1px solid var(--border)',
        padding: '3rem 0',
        marginBottom: '3rem'
      }}>
        <div className="container">
          <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ fontSize: 'var(--fs-3xl)', fontWeight: 800, marginBottom: '1.5rem', letterSpacing: '-0.02em' }}>
              Universal Tool Discovery System
            </h2>
            <div style={{ position: 'relative', marginBottom: '2rem' }}>
              <Search style={{ position: 'absolute', left: '1.5rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary)', opacity: 0.6 }} size={22} />
              <input 
                type="text" 
                placeholder="Search by tool name, function, or technical requirement..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '1.25rem 1.5rem 1.25rem 3.5rem',
                  borderRadius: '20px',
                  border: '1px solid var(--border)',
                  fontSize: '1.1rem',
                  fontWeight: 500,
                  boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)',
                  outline: 'none',
                  transition: 'all 0.3s'
                }}
                className="search-input"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  style={{ position: 'absolute', right: '1.25rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}
                >
                  <X size={20} />
                </button>
              )}
            </div>

            <div className="category-scroll" style={{
              display: 'flex',
              gap: '0.65rem',
              overflowX: 'auto',
              padding: '0.5rem',
              justifyContent: 'center',
              background: 'rgba(241, 245, 249, 0.5)',
              backdropFilter: 'blur(8px)',
              borderRadius: '24px',
              border: '1px solid rgba(226, 232, 240, 0.8)',
              width: 'fit-content',
              margin: '0 auto'
            }}>
              {CATEGORIES.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.65rem',
                    padding: '0.65rem 1.1rem',
                    borderRadius: '16px',
                    fontSize: '0.875rem',
                    fontWeight: activeCategory === cat.id ? 800 : 600,
                    whiteSpace: 'nowrap',
                    border: '1px solid',
                    borderColor: activeCategory === cat.id ? 'var(--primary)' : 'transparent',
                    background: activeCategory === cat.id ? 'var(--primary)' : 'transparent',
                    color: activeCategory === cat.id ? '#fff' : '#475569',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    boxShadow: activeCategory === cat.id ? '0 10px 20px -5px rgba(37, 99, 235, 0.3)' : 'none'
                  }}
                  className={activeCategory === cat.id ? '' : 'hover-glass'}
                >
                  <span style={{ opacity: activeCategory === cat.id ? 1 : 0.7 }}>{cat.icon}</span>
                  <span>{cat.id}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Results Metadata */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <span style={{ fontWeight: 800, fontSize: '1.1rem', color: '#1e293b' }}>
              {filteredTools.length} Tools Available
            </span>
            {activeCategory !== 'All Utilities' && (
              <span style={{ marginLeft: '0.5rem', color: '#64748b' }}>in {activeCategory}</span>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', background: '#f1f5f9', padding: '0.25rem', borderRadius: '10px' }}>
            <button 
              onClick={() => setViewMode('grid')}
              style={{ padding: '0.5rem', borderRadius: '8px', background: viewMode === 'grid' ? '#fff' : 'transparent', color: viewMode === 'grid' ? 'var(--primary)' : '#64748b', border: 'none', cursor: 'pointer', boxShadow: viewMode === 'grid' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}
            >
              <LayoutGrid size={18} />
            </button>
            <button 
              onClick={() => setViewMode('list')}
              style={{ padding: '0.5rem', borderRadius: '8px', background: viewMode === 'list' ? '#fff' : 'transparent', color: viewMode === 'list' ? 'var(--primary)' : '#64748b', border: 'none', cursor: 'pointer', boxShadow: viewMode === 'list' ? '0 2px 4px rgba(0,0,0,0.05)' : 'none' }}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Tools Grid/List */}
        {filteredTools.length > 0 ? (
          <div style={{ 
            display: viewMode === 'grid' ? 'grid' : 'flex',
            flexDirection: viewMode === 'grid' ? 'unset' : 'column',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '1.5rem',
            marginBottom: '4rem'
          }}>
            {filteredTools.map((tool) => (
              <Link 
                key={tool.title} 
                href={tool.href}
                className="tool-card hover-lift"
                style={{
                  background: '#fff',
                  border: '1px solid var(--border)',
                  borderRadius: '24px',
                  padding: '1.75rem',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'flex',
                  flexDirection: viewMode === 'grid' ? 'column' : 'row',
                  alignItems: viewMode === 'grid' ? 'flex-start' : 'center',
                  gap: '1.25rem',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                <div style={{
                  width: '56px',
                  height: '56px',
                  background: '#f8fafc',
                  borderRadius: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  {tool.icon}
                </div>
                
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 800, margin: 0, color: '#0f172a' }}>{tool.title}</h3>
                    {tool.isNew && <span style={{ background: 'var(--primary)', color: '#fff', fontSize: '0.65rem', fontWeight: 900, padding: '0.2rem 0.5rem', borderRadius: '6px', letterSpacing: '0.05em' }}>NEW</span>}
                  </div>
                  <p style={{ fontSize: '0.925rem', color: '#64748b', lineHeight: 1.6, margin: 0, display: '-webkit-box', WebkitLineClamp: viewMode === 'grid' ? '2' : '1', WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {tool.description}
                  </p>
                </div>

                <div className="tool-card-arrow" style={{
                  marginLeft: 'auto',
                  opacity: 0.3,
                  transition: 'all 0.2s'
                }}>
                  <ArrowRight size={20} />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '6rem 2rem', background: '#f8fafc', borderRadius: '32px', border: '2px dashed #e2e8f0' }}>
            <div style={{ display: 'inline-flex', width: '80px', height: '80px', background: '#fff', borderRadius: '50%', alignItems: 'center', justifyItems: 'center', justifyContent: 'center', marginBottom: '1.5rem', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.05)' }}>
              <Search size={32} color="#94a3b8" />
            </div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#1e293b', marginBottom: '0.5rem' }}>No matching tools found</h3>
            <p style={{ color: '#64748b', maxWidth: '400px', margin: '0 auto 2rem' }}>
              We couldn&apos;t find any tools matching &ldquo;{searchQuery}&rdquo;. Try adjusting your search or category filter.
            </p>
            <button 
              onClick={() => { setSearchQuery(''); setActiveCategory('All Utilities'); }}
              style={{ padding: '0.75rem 2rem', background: 'var(--primary)', color: '#fff', border: 'none', borderRadius: '12px', fontWeight: 700, cursor: 'pointer' }}
            >
              Reset All Filters
            </button>
          </div>
        )}

        {/* Featured / Recommended */}
        {searchQuery === '' && activeCategory === 'All Utilities' && (
          <div style={{ marginTop: '2rem', padding: '3rem', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', borderRadius: '32px', color: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Sparkles size={24} color="var(--primary)" />
                <h3 style={{ fontSize: '1.75rem', fontWeight: 800, margin: 0 }}>Recently Deployed</h3>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
              {featuredTools.map(tool => (
                <Link key={tool.href} href={tool.href} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '1.5rem', textDecoration: 'none', color: 'inherit' }} className="hover-scale">
                  <div style={{ marginBottom: '1rem' }}>{tool.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: '0.5rem' }}>{tool.title}</div>
                  <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>{tool.description}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .category-scroll::-webkit-scrollbar {
          display: none;
        }
        .category-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .hover-glass:hover {
          background: rgba(255, 255, 255, 0.8) !important;
          color: var(--primary) !important;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
        }
        .tool-card:hover .tool-card-arrow {
          opacity: 1;
          transform: translateX(5px);
          color: var(--primary);
        }
        .tool-card:hover {
          border-color: var(--primary);
          box-shadow: 0 20px 40px -10px rgba(37, 99, 235, 0.1);
        }
      `}</style>
    </div>
  );
}
